import { styled } from '@linaria/react';
import { mdiOpenInNew } from '@mdi/js';
import Icon from '@mdi/react';
import { Link as GatsbyLink, GatsbyLinkProps } from 'gatsby';
import * as React from 'react';
import { prepareAttribute } from '../utils/helpers';

const StyledLink = styled.a`
  > svg {
    margin-left: 5px;
  }
`;

interface AttributeProps {
  [key: string]: any;
}

interface LinkPropsExtended {}

export type LinkProps = Omit<GatsbyLinkProps<{}>, 'ref'> & LinkPropsExtended;

const isInternalLink = (to: string): boolean => /^\/(?!\/)/.test(to);
const isFileLink = (to: string): boolean =>
  /^\/sites\/default\/files\/.*\.[0-9a-z]+(?=\?|$)/i.test(to);
const isExternalLink = (to: string): boolean => /^https?:\/\//.test(to);

const getTarget = (to: string): '_blank' | '_self' =>
  isExternalLink(to) || isFileLink(to) ? '_blank' : '_self';

const Link: React.FC<LinkProps> = function ({ children, to, ...props }) {
  const internal = isInternalLink(to);
  const file = isFileLink(to);

  const target = getTarget(to);

  const attributes: AttributeProps = {};

  // Rewrite attributes to valid html.
  Object.entries(props).forEach(attribute => {
    const key: string = prepareAttribute(attribute[0]);
    attributes[key] = attribute[1];
  });

  attributes.className = attributes.className ? `${attributes.className} js-track` : 'js-track';
  attributes.target = target;

  if (target === '_blank') {
    attributes.rel = 'noopener noreferrer';
  }

  const withIcon = attributes?.icon;

  // INTERNAL link
  if (internal && target === '_self') {
    return (
      <GatsbyLink to={to} {...attributes}>
        {children}
      </GatsbyLink>
    );
  }

  // EXTERNAL or FILE link
  return (
    <StyledLink href={to} {...attributes}>
      {children}
      {target === '_blank' && !withIcon && !file && (
        <Icon title="Open in new window" path={mdiOpenInNew} size={0.6} />
      )}
    </StyledLink>
  );
};

export default Link;
